import { useState, useEffect } from "react";
import PublicLayout from "../components/PublicLayout";
import { Server, Activity, Cpu, ArrowRight } from "lucide-react";

export default function StatusPage() {
    const [latencies, setLatencies] = useState({
        ingress: 18,
        websocket: 22,
        portal: 41,
        iad: 12,
        fra: 75,
        syd: 145
    });

    // Simulate minor live fluctuation in latencies to make page feel "alive"
    useEffect(() => {
        const t = setInterval(() => {
            setLatencies((prev) => ({
                ingress: Math.max(10, Math.min(45, prev.ingress + (Math.random() > 0.5 ? 2 : -2))),
                websocket: Math.max(15, Math.min(50, prev.websocket + (Math.random() > 0.5 ? 1 : -1))),
                portal: Math.max(30, Math.min(65, prev.portal + (Math.random() > 0.5 ? 3 : -3))),
                iad: Math.max(8, Math.min(25, prev.iad + (Math.random() > 0.5 ? 2 : -2))),
                fra: Math.max(68, Math.min(90, prev.fra + (Math.random() > 0.5 ? 3 : -3))),
                syd: Math.max(130, Math.min(160, prev.syd + (Math.random() > 0.5 ? 4 : -4)))
            }));
        }, 3000);
        return () => clearInterval(t);
    }, []);

    const services = [
        { name: "HTTP Ingress Router", uptime: "99.98%", stat: "OPERATIONAL", latency: `${latencies.ingress}ms` },
        { name: "WS Connection Relay", uptime: "99.99%", stat: "OPERATIONAL", latency: `${latencies.websocket}ms` },
        { name: "SaaS Portal Dashboard", uptime: "100.00%", stat: "OPERATIONAL", latency: `${latencies.portal}ms` },
        { name: "Authentication Gateway", uptime: "100.00%", stat: "OPERATIONAL", latency: "14ms" }
    ];

    const nodes = [
        { code: "relay-iad-01", region: "US East (Virginia)", uptime: "99.99%", latency: `${latencies.iad}ms` },
        { code: "relay-fra-04", region: "EU Central (Frankfurt)", uptime: "99.98%", latency: `${latencies.fra}ms` },
        { code: "relay-syd-02", region: "AP South (Sydney)", uptime: "99.95%", latency: `${latencies.syd}ms` }
    ];

    return (
        <PublicLayout>
            <div className="max-w-[1000px] mx-auto px-6 py-16">
                
                {/* Status Title Banner */}
                <div className="border border-[#00FF41] bg-[#00FF41]/5 p-8 mb-12 text-center relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(#00FF41_1px,transparent_1px)]" style={{ backgroundSize: "12px 12px" }} />
                    <div className="crt-flicker inline-flex items-center gap-2 text-[#00FF41] mb-2 font-bold">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#00FF41] animate-ping" />
                        ALL SYSTEMS OPERATIONAL
                    </div>
                    <h1 className="text-3xl font-display font-black tracking-tight text-[#FAFAFA] uppercase">
                        SYNAPSELINK SYSTEM METRICS
                    </h1>
                    <p className="text-[#A1A1AA] text-xs mt-2 font-mono uppercase">
                        Current local monitoring loopback active · Uptime over last 90 days
                    </p>
                </div>

                {/* Core Services Uptime */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-6 mb-8">
                    <div className="flex items-center gap-2 border-b border-[#27272A] pb-3 mb-6">
                        <Activity className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-bold text-sm uppercase tracking-wider text-[#FAFAFA]">Core Infrastructure Services</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        {services.map((s, idx) => (
                            <div key={idx} className="border border-[#27272A] bg-[#050505] p-4 flex flex-col justify-between">
                                <div className="flex justify-between items-start">
                                    <span className="text-xs font-bold text-[#FAFAFA]">{s.name}</span>
                                    <span className="text-[10px] text-[#00FF41] font-bold tracking-widest">{s.stat}</span>
                                </div>
                                <div className="flex justify-between items-center mt-6 text-[11px]">
                                    <div className="flex items-center gap-4 text-[#A1A1AA]">
                                        <span>Uptime: <strong className="text-[#FAFAFA]">{s.uptime}</strong></span>
                                        <span>Latency: <strong className="text-[#FAFAFA]">{s.latency}</strong></span>
                                    </div>
                                    {/* Mock 90-day status bar visual */}
                                    <div className="flex gap-0.5">
                                        {[...Array(12)].map((_, i) => (
                                            <span key={i} className="w-1 h-3.5 bg-[#00FF41]" />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Regional Edge Nodes */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-6 mb-8">
                    <div className="flex items-center gap-2 border-b border-[#27272A] pb-3 mb-6">
                        <Server className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-bold text-sm uppercase tracking-wider text-[#FAFAFA]">Global Relay Nodes</h2>
                    </div>

                    <div className="space-y-4">
                        {nodes.map((node, idx) => (
                            <div key={idx} className="border border-[#27272A] bg-[#050505] p-4 flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <div className="font-bold text-xs text-[#FAFAFA]">{node.code}</div>
                                    <div className="text-[10px] text-[#52525B] mt-0.5">{node.region}</div>
                                </div>
                                <div className="flex items-center gap-8 text-[11px] text-[#A1A1AA]">
                                    <div>UPTIME: <strong className="text-[#FAFAFA]">{node.uptime}</strong></div>
                                    <div>LATENCY: <strong className="text-[#FAFAFA]">{node.latency}</strong></div>
                                    <div className="flex gap-0.5">
                                        {[...Array(16)].map((_, i) => (
                                            <span key={i} className="w-1 h-3 bg-[#00FF41]" />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Incident Log History */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-6">
                    <div className="flex items-center gap-2 border-b border-[#27272A] pb-3 mb-6">
                        <Cpu className="w-5 h-5 text-[#FF9900]" />
                        <h2 className="font-bold text-sm uppercase tracking-wider text-[#FAFAFA]">Recent Incidents (90 Days)</h2>
                    </div>

                    <div className="space-y-4 text-xs">
                        <div className="border-l-2 border-l-[#00FF41] pl-4 py-1">
                            <div className="flex items-center justify-between">
                                <span className="font-bold text-[#FAFAFA]">May 29, 2026</span>
                                <span className="text-[10px] uppercase text-[#00FF41]">RESOLVED</span>
                            </div>
                            <p className="text-[#A1A1AA] mt-1">Minor WebSocket relay latency spikes on EU nodes. Resolved via route migrations.</p>
                        </div>
                        <div className="border-l-2 border-l-[#00FF41] pl-4 py-1">
                            <div className="flex items-center justify-between">
                                <span className="font-bold text-[#FAFAFA]">May 12, 2026</span>
                                <span className="text-[10px] uppercase text-[#00FF41]">RESOLVED</span>
                            </div>
                            <p className="text-[#A1A1AA] mt-1">Ingress router software patch successfully deployed. Zero downtime observed.</p>
                        </div>
                        <div className="text-center text-[#52525B] text-[10px] uppercase tracking-wider pt-4 border-t border-[#18181B]">
                            End of incident logs
                        </div>
                    </div>
                </div>

            </div>
        </PublicLayout>
    );
}
